import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Search, Person, Clear } from '@mui/icons-material';
import { GetWorkerVm } from 'api/management/get-worker.vm';
import { getWorkers } from 'api/management/get-workers';

interface WorkerSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (worker: GetWorkerVm | null) => void;
  selectedWorkerId?: string;
  title?: string;
}

const WorkerSelectionDialog: React.FC<WorkerSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
  selectedWorkerId,
  title = 'Selecionar Operador',
}) => {
  const [workers, setWorkers] = useState<GetWorkerVm[]>([]);
  const [filteredWorkers, setFilteredWorkers] = useState<GetWorkerVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchWorkers = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getWorkers(offset || '', {
      success: (response) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        const newWorkers = reset ? response.objects : [...workers, ...response.objects];
        setWorkers(newWorkers);
        setFilteredWorkers(newWorkers);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter operadores:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchWorkers(null, true);
    }
  }, [open]);

  useEffect(() => {
    const filtered = workers.filter(
      (worker) =>
        worker.name.toLowerCase().includes(searchTerm.toLowerCase()) || worker.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredWorkers(filtered);
  }, [searchTerm, workers]);

  const handleSelect = (worker: GetWorkerVm) => {
    onSelect(worker);
    onClose();
  };

  const handleClear = () => {
    onSelect(null);
    onClose();
  };

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset || loading) return;
    fetchWorkers(nextOffset);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Person />
        {title}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="Pesquisar operador"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          margin="normal"
          variant="outlined"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={{ mb: 2 }} />

        <Box>
          {filteredWorkers.length === 0 && !loading ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Person sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body1" color="text.secondary">
                Nenhum operador encontrado
              </Typography>
              {searchTerm && (
                <Typography variant="body2" color="text.disabled">
                  Tente alterar os termos de pesquisa
                </Typography>
              )}
            </Box>
          ) : (
            <List
              sx={{
                maxHeight: 350,
                overflow: 'auto',
                '& .MuiListItemButton-root': {
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  },
                },
              }}
            >
              {filteredWorkers.map((worker) => (
                <ListItemButton key={worker.id} onClick={() => handleSelect(worker)} selected={worker.id === selectedWorkerId}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>{worker.name.charAt(0).toUpperCase()}</Avatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={worker.id === selectedWorkerId ? 600 : 400}>
                        {worker.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        @{worker.username}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}

              {hasMore && searchTerm === '' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Button onClick={handleLoadMore} disabled={loading} variant="outlined" size="small" sx={{ minWidth: 120 }}>
                    {loading ? <CircularProgress size={20} /> : 'Carregar Mais'}
                  </Button>
                </Box>
              )}
            </List>
          )}
        </Box>

        {loading && filteredWorkers.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              A carregar operadores...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button onClick={handleClear} variant="outlined" color="warning" startIcon={<Clear />} sx={{ minWidth: 140 }}>
          Limpar Seleção
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkerSelectionDialog;
